import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Package, RefreshCw } from "lucide-react";
import { ProductImage } from "@/components/catalog/ProductImage";
import { PagePlaceholder } from "@/components/common/PagePlaceholder";
import { orderApi, type Order, type OrderStatus } from "@/features/commerce/api";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

const TAB_DEFS: Array<{ key: "active" | "delivered" | "cancelled"; label: string }> = [
    { key: "active", label: "Active" },
    { key: "delivered", label: "Delivered" },
    { key: "cancelled", label: "Cancelled" },
];

function bucket(order: Order): "active" | "delivered" | "cancelled" {
    if (order.status === "delivered") return "delivered";
    if (order.status === "cancelled") return "cancelled";
    return "active";
}

const STATUS_LABEL: Record<OrderStatus, string> = {
    pending_payment: "Payment pending",
    placed: "Order placed",
    confirmed: "Confirmed",
    packing: "Packing",
    out_for_delivery: "Out for delivery",
    delivered: "Delivered",
    cancelled: "Cancelled",
};

function fINR(n: number) {
    return `₹${n.toLocaleString("en-IN")}`;
}

export default function Orders() {
    const [tab, setTab] = useState<"active" | "delivered" | "cancelled">("active");
    const { data, isLoading, refetch } = useQuery({
        queryKey: ["orders"],
        queryFn: orderApi.list,
        refetchInterval: (query) => {
            const orders = query.state.data;
            if (!orders) return false;
            return orders.some((o) => bucket(o) === "active") ? 8000 : false;
        },
    });

    const filtered = useMemo(() => (data ?? []).filter((o) => bucket(o) === tab), [data, tab]);
    const counts = useMemo(() => {
        const acc = { active: 0, delivered: 0, cancelled: 0 };
        (data ?? []).forEach((o) => {
            acc[bucket(o)] += 1;
        });
        return acc;
    }, [data]);

    return (
        <div data-testid="orders-page" className="tsgo-page">
            <section className="tsgo-container pt-6 sm:pt-10">
                <div className="flex items-end justify-between gap-3">
                    <div>
                        <h1 className="tsgo-h1">Your orders</h1>
                        <p className="tsgo-lead mt-2">Track deliveries and reorder your favourites.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground/70 hover:border-brand/40 hover:text-brand"
                        data-testid="orders-refresh"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </button>
                </div>

                <div className="mt-6 flex gap-2 overflow-x-auto pb-1" role="tablist">
                    {TAB_DEFS.map((t) => (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={tab === t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`orders-tab-${t.key}`}
                            className={cn(
                                "shrink-0 rounded-full border px-4 py-1.5 text-sm font-semibold transition",
                                tab === t.key
                                    ? "border-brand bg-brand text-white"
                                    : "border-border bg-card text-foreground/70 hover:border-brand/40",
                            )}
                        >
                            {t.label} · {counts[t.key]}
                        </button>
                    ))}
                </div>
            </section>

            <section className="tsgo-container mt-6">
                {isLoading ? (
                    <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-32 animate-pulse rounded-2xl border border-border/60 bg-card" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <PagePlaceholder
                        testId="orders-empty"
                        icon={<Package className="h-6 w-6" />}
                        title={tab === "active" ? "No active orders" : tab === "delivered" ? "No delivered orders yet" : "No cancelled orders"}
                        description={tab === "active" ? "Your next order will show up here in real time." : "Delivered orders show up here after arrival."}
                    />
                ) : (
                    <ul className="grid gap-3">
                        {filtered.map((o) => (
                            <li key={o.id}>
                                <Link
                                    to={ROUTES.orderDetailBuild(o.id)}
                                    data-testid={`order-card-${o.id}`}
                                    className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card p-4 transition hover:-translate-y-0.5 hover:shadow-soft sm:flex-row sm:items-center"
                                >
                                    <div className="flex -space-x-2">
                                        {o.items.slice(0, 3).map((it) => (
                                            <ProductImage
                                                key={it.product_id}
                                                product={it}
                                                size="thumb"
                                                className="h-12 w-12 rounded-full border-2 border-background"
                                            />
                                        ))}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                            {o.number}
                                        </p>
                                        <p className="line-clamp-1 text-sm font-semibold">
                                            {o.items.map((i) => i.name).join(" · ")}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {new Date(o.placed_at).toLocaleString()}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-base font-extrabold">{fINR(o.breakdown.total)}</p>
                                        <p
                                            className={cn(
                                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide",
                                                o.status === "delivered"
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : o.status === "cancelled"
                                                      ? "bg-error/10 text-error"
                                                      : "bg-brand/10 text-brand",
                                            )}
                                            data-testid={`order-status-${o.id}`}
                                        >
                                            {STATUS_LABEL[o.status]}
                                        </p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
